import { LegalLink, LegalSection } from "../LegalPage";

/** Fecha de la versión vigente. La muestran /terms y el diálogo del checkbox de registro. */
export const TERMS_UPDATED_AT = "28 de septiembre de 2026";

/**
 * El texto de los Términos y Condiciones, UNA sola vez. Lo usan la página /terms y el diálogo
 * que se abre desde la casilla "Acepto los Términos…" (registro, inscripción directa y checkout).
 *
 * Antes el diálogo tenía su propio texto provisorio ("Este contenido se actualizará
 * próximamente…", y en tuteo): el alumno aceptaba un placeholder mientras /terms ya tenía los
 * términos completos. Con un solo componente, lo que se acepta es lo que se publica.
 *
 * `inDialog`: adentro del diálogo, los links a otras páginas se abren en una pestaña nueva —
 * navegar ahí mismo sacaría al alumno del formulario que está completando.
 */
export function TermsContent({ inDialog = false }: { inDialog?: boolean }) {
  return (
    <>
      <LegalSection title="1. Quiénes somos y qué es esto">
        <p>
          AcademIA (cursosacademia.ar) es una plataforma de cursos online de inteligencia artificial
          en español, operada por Rodrigo González, CUIT 20-35640921-4, con domicilio en Av. Alcear
          1463, Ciudad Autónoma de Buenos Aires, Argentina (en adelante, «AcademIA», «nosotros»). Al
          crear una cuenta o usar la plataforma aceptás estos términos. Si no estás de acuerdo con
          algo, escribinos antes de usarla:{" "}
          <a className="font-bold text-landing-red" href="mailto:hola@cursosacademia.ar">
            hola@cursosacademia.ar
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection title="2. Tu cuenta">
        <p>
          La cuenta se crea y se accede con tu número de WhatsApp mediante un código de verificación
          de un solo uso. Sos responsable de mantener el control de tu teléfono y de no compartir
          los códigos que recibís. La cuenta es personal e intransferible.
        </p>
        <p>
          Podés pedir la baja de tu cuenta en cualquier momento escribiéndonos; ver también la{" "}
          <LegalLink to="/privacy" newTab={inDialog}>
            Política de Privacidad
          </LegalLink>
          .
        </p>
        <p>
          Qué datos usamos, qué cookies ponemos y con qué plataformas de analítica y publicidad los
          compartimos (sólo si nos das permiso) está en la{" "}
          <LegalLink to="/privacy#cookies" newTab={inDialog}>
            Política de Privacidad
          </LegalLink>
          . Tu permiso lo cambiás cuando quieras desde «Preferencias de cookies», al pie de cada
          página.
        </p>
      </LegalSection>

      <LegalSection title="3. Cursos gratuitos y cursos pagos">
        <p>
          Algunos cursos son gratuitos y otros son pagos. El precio de cada curso pago se muestra
          antes de la compra. Los pagos se procesan a través de proveedores de pago externos (por
          ejemplo, Mercado Pago); no almacenamos datos de tarjetas.
        </p>
        <p>
          El acceso a un curso comprado es personal, por tiempo indeterminado mientras la plataforma
          esté operativa, y no incluye derecho a descargar, copiar ni redistribuir el contenido.
        </p>
      </LegalSection>

      <LegalSection title="4. Pre-inscripciones y descuentos de lanzamiento">
        <p>
          Pre-inscribirte a un curso «próximamente» es gratuito y no implica obligación de compra.
          Al pre-inscribirte: (a) te avisamos por WhatsApp cuando el curso esté disponible, y (b)
          obtenés el descuento de lanzamiento publicado (por ejemplo, 80%) sobre el precio de lista,
          válido por el período que se indique en el aviso de lanzamiento. Los descuentos de
          pre-inscripción son personales y no acumulables con otras promociones.
        </p>
      </LegalSection>

      <LegalSection title="5. Certificados">
        <p>
          Al completar un curso o etapa de carrera emitimos un certificado digital con un código de
          verificación único, consultable públicamente. Los certificados acreditan la finalización
          del curso en AcademIA; no constituyen títulos oficiales ni habilitaciones profesionales.
        </p>
      </LegalSection>

      <LegalSection title="6. Uso aceptable">
        <ul>
          <li>No compartas tu cuenta ni revendas el acceso a los cursos.</li>
          <li>No copies, publiques ni redistribuyas el contenido de los cursos.</li>
          <li>No uses la plataforma para actividades ilegales o para dañar a terceros.</li>
          <li>
            No intentes vulnerar la seguridad de la plataforma ni acceder a datos de otros usuarios.
          </li>
        </ul>
        <p>El incumplimiento puede derivar en la suspensión o baja de la cuenta.</p>
      </LegalSection>

      <LegalSection title="7. Propiedad intelectual">
        <p>
          El contenido de los cursos, la marca AcademIA, los textos, ilustraciones, videos y el
          software de la plataforma son propiedad de AcademIA o de sus licenciantes. Te otorgamos
          una licencia personal, limitada y no exclusiva para usar el contenido con fines de
          aprendizaje propio.
        </p>
      </LegalSection>

      <LegalSection title="8. Disponibilidad del servicio">
        <p>
          Trabajamos para que la plataforma esté disponible de forma continua, pero no garantizamos
          disponibilidad ininterrumpida (mantenimientos, mejoras o causas ajenas). Durante etapas de
          lanzamiento algunas funciones pueden cambiar o incorporarse gradualmente.
        </p>
      </LegalSection>

      <LegalSection title="9. Responsabilidad">
        <p>
          Los cursos tienen fines educativos. Las decisiones que tomes aplicando lo aprendido son
          tuyas. En la máxima medida permitida por la ley aplicable, nuestra responsabilidad total
          frente a vos se limita al monto que pagaste por el servicio en cuestión.
        </p>
      </LegalSection>

      <LegalSection title="10. Cambios a estos términos">
        <p>
          Podemos actualizar estos términos. Si el cambio es relevante, lo comunicaremos por la
          plataforma o por WhatsApp con anticipación razonable. La fecha de «última actualización»
          siempre indica la versión vigente.
        </p>
      </LegalSection>

      <LegalSection title="11. Ley aplicable y contacto">
        <p>
          Estos términos se rigen por las leyes de la República Argentina. Ante cualquier duda o
          reclamo, escribinos a{" "}
          <a className="font-bold text-landing-red" href="mailto:hola@cursosacademia.ar">
            hola@cursosacademia.ar
          </a>{" "}
          — resolver las cosas hablando siempre es el primer paso.
        </p>
      </LegalSection>
    </>
  );
}
